/* ==========================================================================
   Cleaner -- find work
   The feed of homes actively looking, sorted by fit, plus today's schedule
   and any job pairs. A home appears here only while it is actively looking.
   ========================================================================== */

import { useRef, useState, type ReactNode } from 'react';
import type { Booking, Cleaner, MatchResult, Pair, PairLeg, Request } from '../../types';
import { Icon } from '../../components/Icon';
import { PullToRefresh } from '../../components/PullToRefresh';
import { JobMap, type MapPin } from '../../components/JobMap';
import {
  Avatar, Card, Empty, JobChips, MatchBar, Note, Pick, SectionHead, Stars, matches,
} from '../../components/ui';
import { REQUESTS } from '../../data/seed';
import {
  appliedCount, findPairs, house, jobLabel, matchScore, milesChip, scheduleFor, shortName, homeTypeLabel, townLabel,
} from '../../lib/domain';
import {
  NOW, TODAY_ISO, addDays, ago, dur, endTime, fmtDate, fmtTime, minsOf, money,
} from '../../lib/time';
import { t, tf } from '../../lib/i18n';
import {
  activeCleaner, blankJobFilter, commit, nav, openSheet, refresh, state, tab,
} from '../../store';

export const JOB_SORTS = [
  { id: 'match', key: 'bestmatch' },
  { id: 'soonest', key: 'soonest' },
  { id: 'pay', key: 'dearest' },
  { id: 'hours', key: 'mosthours' },
  { id: 'near', key: 'nearby' },
] as const;

interface Scored {
  r: Request;
  m: MatchResult;
  areaId: string;
}

export function openRequestsFor(c: Cleaner): Scored[] {
  const f = state.filters.jobs;
  const list = REQUESTS.filter((r) => r.status === 'open')
    .filter((r) => {
      const h = house(r.houseId);
      /* Town or old area tag -- see the note in FindCleaner. Homes that
         predate the postal code carry only the tag. */
      if (f.areas.length && !(f.areas.includes(h.area) || f.areas.includes(h.town ?? ''))) return false;
      if (f.tags.length && !f.tags.some((x) => r.jobIds.includes(x))) return false;
      if (f.minRate && r.rate < f.minRate) return false;
      if (f.minHours && r.hours < f.minHours) return false;
      if (f.date && r.date !== f.date) return false;
      if (f.langs.length && !f.langs.some((l) => h.languages.some((hl) => hl.id === l))) return false;
      if (f.q && !matches(
        [h.displayName, townLabel(h), r.notes, ...r.jobIds.map(jobLabel)].join(' '), f.q,
      )) return false;
      return true;
    })
    .map((r) => ({ r, m: matchScore(c, r), areaId: house(r.houseId).area }));

  /* Miles from the server, which knows both ends. Anything it could not
     place sorts last rather than pretending to be next door. */
  const away = (houseId: string) => house(houseId).milesAway ?? Number.MAX_SAFE_INTEGER;
  const sorters: Record<string, (a: Scored, b: Scored) => number> = {
    match: (a, b) => b.m.score - a.m.score,
    soonest: (a, b) => a.r.date.localeCompare(b.r.date) || minsOf(a.r.start) - minsOf(b.r.start),
    pay: (a, b) => b.r.rate - a.r.rate,
    hours: (a, b) => b.r.hours - a.r.hours,
    near: (a, b) => away(a.r.houseId) - away(b.r.houseId),
  };
  return list.sort(sorters[f.sort] || sorters.match);
}

export function WorkFeed() {
  const c = activeCleaner();
  /* Cleaner-only screen. A home reaching it means a bad link, not a crash. */
  if (!c) return <div className="screen-pad"><Empty big={t('c_cleaneronly')} /></div>;

  const f = state.filters.jobs;
  const list = openRequestsFor(c);
  const pairs = findPairs(c.id);
  /* Only jobs still ahead of you. A banner about this morning's job at 4pm is
     noise, and one about a job you already finished is worse. */
  const nowMins = NOW.getHours() * 60 + NOW.getMinutes();
  const todayJobs = scheduleFor(c.id, TODAY_ISO).filter(
    (b) => b.status === 'confirmed' && minsOf(b.start) + b.hours * 60 > nowMins,
  );
  const activeCount =
    f.areas.length + f.tags.length + f.langs.length +
    (f.minRate ? 1 : 0) + (f.minHours ? 1 : 0) + (f.date ? 1 : 0) + (f.q ? 1 : 0);

  const setDate = (d: string) => {
    f.date = f.date === d ? '' : d;
    commit();
  };

  return (
    <>
      <div className="filterbar">
        <Pick
          on={activeCount > 0}
          onClick={() => openSheet({ name: 'jobfilter' })}
          label={<><Icon name="filter" /> {t('filters')}{activeCount ? ' · ' + activeCount : ''}</>}
        />
        <Pick label={t('today')} on={f.date === TODAY_ISO} onClick={() => setDate(TODAY_ISO)} />
        <Pick
          label={t('tomorrow')}
          on={f.date === addDays(TODAY_ISO, 1)}
          onClick={() => setDate(addDays(TODAY_ISO, 1))}
        />
        <Pick
          onClick={() => openSheet({ name: 'jobsort' })}
          label={<><Icon name="sort" /> {t(JOB_SORTS.find((s) => s.id === f.sort)!.key)}</>}
        />
        {/* Not a filter, so it must not look like one: the chips beside it
            fill in and grow a tick when active, which for a view switch read
            as "List is selected" while you were staring at a map. The label
            and icon name where the tap takes you, and nothing else changes. */}
        <button
          type="button"
          className="pick action"
          onClick={() => { f.mode = f.mode === 'map' ? 'list' : 'map'; commit(); }}
        >
          <Icon name={f.mode === 'map' ? 'brief' : 'map'} />{' '}
          {f.mode === 'map' ? t('list') : t('map')}
        </button>
      </div>

      <PullToRefresh onRefresh={refresh}>
      <div className="screen-pad">
        {todayJobs.length > 0 && <TodayStrip jobs={todayJobs} />}
        {pairs.length > 0 && <PairsCard pairs={pairs} />}

        {f.mode === 'map' ? (
          <MapView list={list} c={c} />
        ) : (
          <>
            <Note>
              <b>
                {f.date
                  ? tf(list.length === 1 ? 'tx_homes_on_1' : 'tx_homes_on_n', { n: list.length, date: fmtDate(f.date, { long: true }) })
                  : tf(list.length === 1 ? 'tx_homes_now_1' : 'tx_homes_now_n', { n: list.length })}
              </b>{' '}
              {t('tx_homes_only_looking')}
            </Note>
            {list.length === 0 && (
              <Empty
                big={t('w_nomatch')}
                sub={t('w_widen')}
                action={t('b_clear')}
                onAction={() => { state.filters.jobs = blankJobFilter(); commit(); }}
              />
            )}
            {/* One column on a phone, several across a desk. A job carries a
                date, a jobs list and a match bar, so it needs more width than
                a person does -- hence roomy. */}
            <div className="cardgrid roomy">
              {groupByHome(list).map((g) =>
                g.length === 1 ? (
                  <RequestCard key={g[0].r.id} r={g[0].r} m={g[0].m} c={c} />
                ) : (
                  <HomeGroup key={g[0].r.houseId} group={g} c={c} />
                ),
              )}
            </div>
          </>
        )}
      </div>
      </PullToRefresh>
    </>
  );
}

/* ------------------------------------------------------------------ today */

function TodayStrip({ jobs }: { jobs: Booking[] }) {
  return (
    <Card
      className="tint"
      onClick={() => { state.calMode = 'today'; tab('c_calendar'); }}
    >
      <SectionHead title={t('today') + ' — ' + t('schedule')} />
      {jobs.map((b) => (
        <div className="row between" key={b.id}>
          <span className="row" style={{ gap: 8 }}>
            <Avatar p={house(b.houseId)} size="sm" />
            <b style={{ fontSize: 14 }}>{house(b.houseId).displayName}</b>
          </span>
          <span className="stat">
            {fmtTime(b.start)}–{endTime(b.start, b.hours)}
          </span>
        </div>
      ))}
    </Card>
  );
}

/* ------------------------------------------------------------------ pairs */

function legName(leg: PairLeg): string {
  const ref = leg.ref as Booking | Request;
  return house(ref.houseId).displayName;
}

/** A pair is a suggestion, and a suggestion you cannot wave away becomes
 *  nagging. Dismissed pairs stay gone until the app is reopened -- long enough
 *  to stop being in the way, short enough that a genuinely good pairing gets
 *  another chance tomorrow. */
function pairKey(p: Pair): string {
  return p.date + '|' + p.first.start + '|' + p.second.start;
}

function PairsCard({ pairs }: { pairs: Pair[] }) {
  const showing = pairs.filter((p) => !state.hiddenPairs.includes(pairKey(p)));
  if (!showing.length) return null;

  return (
    <Card className="warm">
      <div className="row between">
        <SectionHead title={t('h_fittogether')} />
        <button
          type="button"
          className="iconbtn"
          aria-label={t('w_hidesugg')}
          onClick={() => {
            state.hiddenPairs = [
              ...state.hiddenPairs,
              ...showing.map(pairKey),
            ];
            commit();
          }}
        >
          ✕
        </button>
      </div>
      <span className="sub">
        {t('tx_pair_lede')}
      </span>
      {showing.slice(0, 2).map((p, i) => (
        <div key={i}>
          {i > 0 && <div className="divider" />}
          <div className="pair">
            <div className="pair-leg">
              <span className="t">{fmtTime(p.first.start)}</span>
              <span>{legName(p.first)}</span>
              <span className={'chip ' + (p.first.kind === 'booking' ? 'good' : 'tile')}>
                {p.first.kind === 'booking' ? t('booked') : t('tx_open')}
              </span>
            </div>
            <div className="pair-travel">
              <Icon name="car" /> {milesChip(p.miles)} ·{' '}
              {tf('tx_pair_travel', { travel: dur(p.travel), gap: dur(p.gap) })}
            </div>
            <div className="pair-leg">
              <span className="t">{fmtTime(p.second.start)}</span>
              <span>{legName(p.second)}</span>
              <span className={'chip ' + (p.second.kind === 'booking' ? 'good' : 'tile')}>
                {p.second.kind === 'booking' ? t('booked') : t('tx_open')}
              </span>
            </div>
            {p.second.kind === 'request' && (
              <button
                type="button"
                className="btn sm"
                onClick={() => nav('c_job', { id: (p.second.ref as Request).id })}
              >
                {t('w_seejob')}
              </button>
            )}
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ------------------------------------------------------------------- card */

/* One home, several dates. Five cards from the same family push everybody
   else off the screen, and reading the same name five times tells a cleaner
   nothing new. Grouped, the home is one thing you swipe through.

   Order is preserved: the group sits where that home's best-fitting request
   would have sat on its own, so nothing jumps up the feed by posting twice. */
function groupByHome(list: Scored[]): Scored[][] {
  const groups: Scored[][] = [];
  const at = new Map<string, number>();
  for (const item of list) {
    const seen = at.get(item.r.houseId);
    if (seen === undefined) {
      at.set(item.r.houseId, groups.length);
      groups.push([item]);
    } else {
      groups[seen].push(item);
    }
  }
  return groups;
}

function HomeGroup({ group, c }: { group: Scored[]; c: Cleaner }) {
  const [at, setAt] = useState(0);
  const i = Math.min(at, group.length - 1);
  const touch = useRef<{ x: number; y: number } | null>(null);

  const go = (d: number) =>
    setAt((n) => Math.max(0, Math.min(group.length - 1, n + d)));

  return (
    <div
      className="swipe-group"
      /* Arrow keys work everywhere a keyboard does. The group takes focus so
         they have somewhere to land, but is not in the tab order: the cards
         and dots inside it are the things worth tabbing to. */
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      }}
      onTouchStart={(e) => {
        touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }}
      onTouchEnd={(e) => {
        const from = touch.current;
        touch.current = null;
        if (!from) return;
        const dx = e.changedTouches[0].clientX - from.x;
        const dy = e.changedTouches[0].clientY - from.y;
        /* Only a sideways swipe. Without the comparison, scrolling the feed
           with a slightly crooked thumb flips the card underneath you. */
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          go(dx < 0 ? 1 : -1);
        }
      }}
    >
      {/* Every card in the group is rendered into the same grid cell, so the
          stack is always as tall as its tallest member and swiping does not
          make the page jump. A weekly job carries an extra line, which is
          exactly the case that used to lurch. */}
      <div className="swipe-stack">
        {group.map((g, n) => (
          <div key={g.r.id} className={'swipe-slide' + (n === i ? ' on' : '')} aria-hidden={n !== i}>
            <RequestCard
              r={g.r}
              m={g.m}
              c={c}
              pager={<Pager total={group.length} at={i} onPick={setAt} onStep={go} />}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Which of this home's dates you are looking at.

    Inside the card, because it belongs to the card.

    Two controls, each for the input it suits. On a phone this is a swipe and
    the dots say where you are; a pair of chevrons there would only announce
    "this is a website". On a desk there is nothing to swipe with, so the
    chevrons appear -- CSS shows them from 1024px up, which is the same line
    everything else uses to stop being a phone. Arrow keys work either way. */
function Pager({
  total, at, onPick, onStep,
}: {
  total: number; at: number; onPick: (i: number) => void; onStep?: (d: number) => void;
}) {
  const step = (e: React.MouseEvent, d: number) => { e.stopPropagation(); onStep?.(d); };

  return (
    <div className="pager">
      <button
        type="button"
        className="pager-arrow"
        aria-label={t('w_prevdate')}
        disabled={at === 0}
        onClick={(e) => step(e, -1)}
      >
        <Icon name="back" />
      </button>

      <div className="pager-dots">
        {Array.from({ length: total }, (_, n) => (
          <button
            key={n}
            type="button"
            className={'dot' + (n === at ? ' on' : '')}
            aria-label={'Date ' + (n + 1) + ' of ' + total}
            aria-current={n === at}
            onClick={(e) => { e.stopPropagation(); onPick(n); }}
          />
        ))}
      </div>
      <span className="pager-say">{tf('tx_n_of_m', { n: at + 1, m: total })}</span>

      <button
        type="button"
        className="pager-arrow"
        aria-label={t('w_nextdate')}
        disabled={at === total - 1}
        onClick={(e) => step(e, 1)}
      >
        <Icon name="fwd" />
      </button>
    </div>
  );
}

export function RequestCard({
  r, m, c, pager,
}: { r: Request; m: MatchResult; c: Cleaner; pager?: ReactNode }) {
  const h = house(r.houseId);
  const mine = r.applicants.find((a) => a.cleanerId === c.id);

  return (
    <Card recur={!!r.recurring} onClick={() => nav('c_job', { id: r.id })}>
      <div className="row top">
        <Avatar p={h} />
        <div className="col grow">
          <div className="row between">
            <span className="name">{h.displayName}</span>
            <span className="rate">{money(r.rate)}<small>{t('perhour')}</small></span>
          </div>
          <span className="sub">
            {[homeTypeLabel(h.homeType), tf('tx_beds', { n: h.bedrooms }), townLabel(h)].filter(Boolean).join(' · ')}
          </span>
          <div className="row" style={{ gap: 6, marginTop: 3 }}>
            <Stars value={h.ratings.overall} />
            <span className="sub tight">{h.ratings.count} {t('reviews')}</span>
          </div>
        </div>
      </div>

      <div className="row wrap" style={{ gap: 6 }}>
        <span className="chip warm">
          {fmtDate(r.date)} · {fmtTime(r.start)}–{endTime(r.start, r.hours)}
        </span>
        <span className="chip">{r.hours} {t('hours')}</span>
        {!r.recurring && <span className="chip outline">{t('onetime')}</span>}
        {h.milesAway !== null && <span className="chip">{milesChip(h.milesAway)}</span>}
      </div>

      <div className="chips"><JobChips ids={r.jobIds} max={4} /></div>
      <div className="divider" />

      <div className="col" style={{ gap: 6 }}>
        <div className="row between">
          <span className="sub tight" style={{ fontWeight: 700, color: 'var(--tile)' }}>
            {m.score}% {t('match')}
          </span>
          <span className="sub tight">
            {appliedCount(r)} {t('applied')} · {ago(r.posted)}
          </span>
        </div>
        <MatchBar score={m.score} />
        <span className="sub tight">{m.reasons.slice(0, 2).join(' · ')}</span>
      </div>

      {mine && (
        <span className="chip good">
          {mine.status === 'counter'
            ? tf('tx_you_countered', { rate: money(mine.counterRate!) })
            : mine.status === 'invited'
              ? t('tx_they_invited')
              : t('tx_you_applied')}
        </span>
      )}

      {pager}
    </Card>
  );
}

/* -------------------------------------------------------------------- map */

function MapView({ list, c }: { list: Scored[]; c: Cleaner }) {
  const placed = list.filter(({ r }) => {
    const h = house(r.houseId);
    return typeof h.lat === 'number' && typeof h.lon === 'number';
  });
  const unplaced = list.length - placed.length;

  const pins: MapPin[] = placed.map(({ r }) => {
    const h = house(r.houseId);
    return {
      id: r.id,
      lat: h.lat as number,
      lon: h.lon as number,
      label: shortName(h.displayName),
      badge: '$' + Math.round(r.rate),
      initials: h.initials,
      hue: h.hue,
    };
  });

  return (
    <>
      <JobMap
        pins={pins}
        me={{ lat: c.lat, lon: c.lon }}
        radiusMiles={c.radiusMiles}
        onPick={(id) => nav('c_job', { id })}
      />

      <Note>
        <b>{tf(placed.length === 1 ? 'tx_reach_1' : 'tx_reach_n', { n: placed.length })}</b>{' '}
        {t('tx_map_help')}
        {unplaced > 0 && ' ' + tf('tx_unplaced', { n: unplaced })}
      </Note>

      <div className="cardgrid roomy">
        {list.slice(0, 3).map(({ r, m }) => (
          <RequestCard key={r.id} r={r} m={m} c={c} />
        ))}
      </div>
    </>
  );
}
